import { Excalidraw, exportToBlob } from "@excalidraw/excalidraw"
import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { ExcalidrawImperativeAPI, ExcalidrawInitialDataState } from "@excalidraw/excalidraw/types/types"
import Spinner from 'react-bootstrap/Spinner'
import styles from "./DrawingQuestion.module.scss"
import { Question } from "shared/lessons"
import { convertBlobToBase64, loadImage } from "../helpers/drawingHelper"
import { LessonContext } from "components/Lessons/LessonContext"
import { CurriculumQuestion } from "shared/types/curriculumTypes"
import { DrawingAnswer } from "shared/types/studentTypes"
import useBoolean from "helpers/useBoolean"
import { removeHighlight } from "helpers/htmlElementHighlight"
import { Button } from "react-bootstrap"
import { AssessmentQuestion } from "shared/routes/assignments"
import { BeatLoader } from "react-spinners"
import CustomHTML from "components/CustomHTML"
import Dropdown from 'react-bootstrap/Dropdown'

export type DrawingQuestionType = "lesson" | "assessment" | "topic"

export type TopicDrawingQuestion = CurriculumQuestion & { sectionNumber: number }
export type LessonDrawingQuestion = Question & { lessonQuestionIndex: string, sectionNumber: number }
export type AssessmentDrawingQuestion = AssessmentQuestion & { questionImagePath: string }

interface Props {
  questionType: DrawingQuestionType
  question: TopicDrawingQuestion | LessonDrawingQuestion | AssessmentDrawingQuestion
  minimizeCanvas: () => void

  // assessment drawing question props
  initialAssessmentDrawingData?: DrawingAnswer
  updateDrawingResponses?: (props: { questionNumber: number, answer: string, base64String: string, updateInitialState?: boolean }) => void

  // topic drawing question props
  handleAnswerChange?: (sectionNum: number, questionsNum: number, answer: string, drawingResponse: string, updateInitialState?: boolean) => void
  initialTopicDrawingData?: DrawingAnswer
}

export function isTopicQuestion(question: Question | CurriculumQuestion | AssessmentQuestion, questionType: DrawingQuestionType ): question is CurriculumQuestion {
  return questionType === "topic"
}

export function isLessonQuestion(question: Question | CurriculumQuestion | AssessmentQuestion, questionType: DrawingQuestionType ): question is (Question & {lessonQuestionIndex: string}) {
  return questionType === "lesson"
}

export function isAssessmentQuestion(question: Question | CurriculumQuestion | AssessmentQuestion, questionType: DrawingQuestionType ): question is AssessmentQuestion {
  return questionType === "assessment"
}

const DrawingQuestion = ({ questionType, initialAssessmentDrawingData, updateDrawingResponses, minimizeCanvas, question, handleAnswerChange, initialTopicDrawingData } : Props) => {
  const [excaliAPI, setExcaliAPI] = useState<ExcalidrawImperativeAPI>(null)
  const [excaliStateElements, setExcaliStateElements] = useState<{state: { elements: readonly unknown[], state: unknown }, base64string: string} | null>(null)
  const [saving, setSaving] = useState(false)
  const [initialData, setInitialData] = useState<ExcalidrawInitialDataState>(null)
  const [hasStartedDrawing, setHasStartedDrawing] = useState<boolean>(false)
  
  const [zenModeEnabled, toggleZenMode] = useBoolean(true)
  const [ loading, toggleLoading ] = useBoolean(false)

  const { drawingResponses, setDrawingResponses } = useContext(LessonContext)

  const saveDrawing = useCallback(async () => {
    if (!excaliAPI) return

    const elements = excaliAPI.getSceneElements()
    const excaliState = {
      elements: elements,
      state: excaliAPI.getAppState()
    }
    let blob: Blob = null

    if (elements.length > 0) {
      blob = await exportToBlob({
        elements: elements,
        mimeType: "image/png",
        appState: {
          ...excaliAPI.getAppState(),
        },
        files: excaliAPI?.getFiles()
      })
    }

    const base64String = blob ? await convertBlobToBase64(blob) : null

    if (isLessonQuestion(question, questionType)) {
      const lessonQuestionKey = `question-${question.sectionNumber}-${question.index}`
      setDrawingResponses(prev => ({...prev, [lessonQuestionKey]: { answer: JSON.stringify(excaliState), drawingResponse: base64String }}))
      if (base64String) {
        removeHighlight(lessonQuestionKey)
      }
    } else if (isAssessmentQuestion(question, questionType)) {
      updateDrawingResponses({ questionNumber: question.number, answer: JSON.stringify(excaliState), base64String: base64String })
    } else if (isTopicQuestion(question, questionType)) {
      handleAnswerChange(question.sectionNumber, question.sortOrder, JSON.stringify(excaliState), base64String)
    }

    setExcaliStateElements({state: {elements: excaliState.elements, state: excaliState.state}, base64string: base64String})
  }, [excaliAPI, handleAnswerChange, question, questionType, updateDrawingResponses, setDrawingResponses])

  const save = useCallback(() => {
    setSaving(true)
    // add a delay for the excali api to get latest scene elements
    setTimeout(() => {
      saveDrawing()
      .then(() => setTimeout(() => setSaving(false), 3000))
    }, 100)
  }, [saveDrawing])

  const keySaveRef = useRef(null)

  const keySave = useCallback(() => {
    if (keySaveRef.current) {
      clearTimeout(keySaveRef.current)
    }
    const timeoutId = setTimeout(() => {
      if (keySaveRef.current === timeoutId) {
        save()
      }
    }, 500)
    keySaveRef.current = timeoutId
  }, [save])

  const onMinimize = useCallback(() => {
    if (isTopicQuestion(question, questionType) && excaliStateElements) {
      handleAnswerChange(question.sectionNumber, question.sortOrder, JSON.stringify(excaliStateElements.state), excaliStateElements.base64string, true)
    }

    if (isAssessmentQuestion(question, questionType) && excaliStateElements) {
      updateDrawingResponses({ questionNumber: question.number, answer: JSON.stringify(excaliStateElements.state), base64String: excaliStateElements.base64string, updateInitialState: true })
    }
    minimizeCanvas()
  }, [excaliStateElements, handleAnswerChange, minimizeCanvas, question, questionType, updateDrawingResponses])

  /**
   * Load initial drawing image (if resuming previous attempt) instead of original lesson question image
   */
  useEffect(() => {
    const noPreviousDrawings = Object.keys(drawingResponses ?? {}).length === 0 || Object.keys(initialAssessmentDrawingData ?? {}).length === 0 || Object.keys(initialTopicDrawingData ?? {}).length === 0
    if (!question && noPreviousDrawings) return

    const questionNum = isLessonQuestion(question, questionType) ? question.index : isTopicQuestion(question, questionType) ? question.sortOrder : question.number
    const fileId = `file-${questionType}-${questionNum}`
    const imageId = `image-${questionType}-${questionNum}`

    if (isLessonQuestion(question, questionType)) {
      const initialDrawingData = drawingResponses?.[`question-${question.sectionNumber}-${question.index}`]
      toggleLoading()
      loadImage({ initialDrawingData, imagePath: question.imagePath, fileId, imageId }).then(res => {
        setInitialData(res)
        toggleLoading()
      })
    } else if (isAssessmentQuestion(question, questionType)) {
      toggleLoading()
      loadImage({ initialDrawingData: initialAssessmentDrawingData ?? null, imagePath: question.questionImagePath, fileId, imageId }).then(res => {
        setInitialData(res)
        toggleLoading()
      })
    } else if (isTopicQuestion(question, questionType)) {
      if (question?.questionImageId && !question?.questionImageUrl) return // this ensures topic question image url is loaded properly
      toggleLoading()
      loadImage({ initialDrawingData: initialTopicDrawingData, imagePath: question.questionImageUrl, fileId, imageId }).then(res => {
        setInitialData(res)
        toggleLoading()
      })
    }
    // eslint-disable-next-line
  }, [initialTopicDrawingData, initialAssessmentDrawingData]) // need to run when initial drawing data is loaded

  useEffect(() => {
    const hideElements = () => {
      setTimeout(() => {
        document.querySelectorAll<HTMLElement>("button[data-testid='main-menu-trigger']").forEach((item) => item.style.display = "none")
        document.querySelectorAll<HTMLElement>("footer.App-toolbar").forEach((item) => item.style.display = "none")
      }, 1000)
    }

    hideElements()
    window.addEventListener("resize", hideElements)

    return () => window.removeEventListener("resize", hideElements)
  }, [])

  if (initialData === null) return <BeatLoader />

  return (
    <>
      <div className="d-flex flex-row align-items-center justify-content-between">
          {isLessonQuestion(question, questionType) && <span>{question.lessonQuestionIndex}. <CustomHTML html={question.content} /></span>}
          {isTopicQuestion(question, questionType) && <span>{question.sortOrder}. <span dangerouslySetInnerHTML={{__html: question.question}} /></span>}
          {isAssessmentQuestion(question, questionType) && <span>{question.number}. <CustomHTML html={question.question} /></span>}
          <i role="button" className={`fas fa-close iconButton me-2 py-1 text-dark`} onClick={onMinimize}/>
      </div>

        {!loading && <div 
          className={styles.canvas}
          onClick={(e: any) => {
            const target = e.target.closest("button")
            if (target && target.tagName === "BUTTON") {
              const fillActions = ["fill-solid", "fill-cross-hatch", "fill-hachure"]
              const arrowActions = ["arrowhead_start", "arrowhead_end"]
              const restrictedClassNames = ["picker-option"]
              if (fillActions.includes(target.getAttribute("data-testid")) || arrowActions.includes(target.getAttribute("aria-label")) || restrictedClassNames.some(className => target.classList?.contains(className))) {
                e.preventDefault()
              }
            }
          }}
          onKeyDownCapture={(e) => {
            keySave()
          }}
          onWheelCapture={e => e.stopPropagation()} // prevent scrolling
          >
            {saving && <span className={styles.loading}>
              <span className="me-2">Saving</span>
                <Spinner animation="border" role="status" size="sm">
                </Spinner>
            </span>}
            <div className={`${styles.hideToolboxBtn}`}>
              <Button variant="primary" size="sm" onClick={toggleZenMode}>{zenModeEnabled ? "Disable Zen Mode" : "Enable Zen Mode"}</Button>
              <Dropdown className={styles.commandsDropdownMenu}>
                <Dropdown.Toggle variant="outline-secondary" size="sm">
                  Commands
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.ItemText><strong>Select all:</strong> <code>Cmd/Ctrl + A</code></Dropdown.ItemText>
                  <Dropdown.ItemText><strong>Undo:</strong> <code>Cmd/Ctrl + Z</code></Dropdown.ItemText>
                  <Dropdown.ItemText><strong>Redo:</strong> <code>Cmd/Ctrl + Shift + Z</code></Dropdown.ItemText>
                  <Dropdown.ItemText><strong>Duplicate selection:</strong> <code>Cmd/Ctrl + D</code></Dropdown.ItemText>
                  <Dropdown.ItemText><strong>Copy/Paste:</strong> <code>Cmd/Ctrl + C / Cmd/Ctrl + V</code></Dropdown.ItemText>
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <Excalidraw
              initialData={initialData}
              onPointerDown={() => setHasStartedDrawing(true)}
              onPointerUpdate={(e) => {
                if (e.button === "up" && hasStartedDrawing) {
                  save()
                  setHasStartedDrawing(false)
                }
              }}
              autoFocus={false}
              excalidrawAPI={api => setExcaliAPI(api)}
              zenModeEnabled={zenModeEnabled}
              viewModeEnabled={false}
              UIOptions={{
                tools: { image: false }
              }}/>
        </div>}
        <div className="m-auto d-flex justify-content-center mt-2 p-3">
        <div className="text-muted">The drawing will be saved automatically as you draw on the canvas.</div>
        </div>
    </>
  )
}

export default DrawingQuestion
